// Carry Roll IndexedDB persistence adapter — sessions, profiles, bag (P2).
// When AppLock is active (encrypted mode), all IDB operations route through
// lock.idb for transparent record-level encryption. When inactive, raw IDB is used.

import { openDB, type IDBPDatabase } from 'idb';
import type { IDBRecordAPI } from '@ascend/applock';
import type { BagEntry, ShotProfile, Session } from '@carry-roll/shared-types';

const DB_NAME = 'carry-roll-db';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase> | null = null;
let idbAdapter: IDBRecordAPI | null = null;

/**
 * Ensure the database schema exists at the correct version before AppLock.init().
 * AppLock opens the DB without a version; we must create object stores first.
 */
export async function ensureDBSchema(): Promise<void> {
  await getDB();
}

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('sessions')) db.createObjectStore('sessions', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('profiles')) db.createObjectStore('profiles', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('bag')) db.createObjectStore('bag', { keyPath: 'clubName' });
      },
    });
  }
  return dbPromise;
}

/** Wire the AppLock IDB record adapter so subsequent calls encrypt/decrypt transparently. */
export function setIDBAdapter(adapter: IDBRecordAPI): void {
  idbAdapter = adapter;
}

/** Remove the adapter (called on lock — key is wiped from memory). */
export function resetIDBAdapter(): void {
  idbAdapter = null;
}

export const cryIDBAdapter = {
  async getAllSessions(): Promise<Session[]> {
    if (idbAdapter) return idbAdapter.getAll<Session>('sessions');
    const db = await getDB();
    return db.getAll('sessions');
  },
  async putSession(session: Session): Promise<void> {
    if (idbAdapter) return idbAdapter.put('sessions', session);
    const db = await getDB();
    await db.put('sessions', session);
  },
  async getAllProfiles(): Promise<ShotProfile[]> {
    if (idbAdapter) return idbAdapter.getAll<ShotProfile>('profiles');
    const db = await getDB();
    return db.getAll('profiles');
  },
  async putProfile(profile: ShotProfile): Promise<void> {
    if (idbAdapter) return idbAdapter.put('profiles', profile);
    const db = await getDB();
    await db.put('profiles', profile);
  },
  async getAllBag(): Promise<BagEntry[]> {
    if (idbAdapter) return idbAdapter.getAll<BagEntry>('bag');
    const db = await getDB();
    return db.getAll('bag');
  },
  async putBagEntry(entry: BagEntry): Promise<void> {
    if (idbAdapter) return idbAdapter.put('bag', entry);
    const db = await getDB();
    await db.put('bag', entry);
  },
  async clearAll(): Promise<void> {
    if (idbAdapter) {
      await idbAdapter.clear('sessions');
      await idbAdapter.clear('profiles');
      await idbAdapter.clear('bag');
      return;
    }
    const db = await getDB();
    await db.clear('sessions');
    await db.clear('profiles');
    await db.clear('bag');
  },
};
